import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import { useCart } from '../context/CartContext';
import { submitOrderRequest } from '../utils/api';

const OrderRequestPage = () => {
  const { cart, cartTotal, emptyCart } = useCart();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cart.map(item => ({
          productId: item.product._id,
          name: item.product.name,
          brand: item.product.brand,
          size: item.product.size,
          price: item.product.discountedPrice || item.product.price,
          quantity: item.quantity
        })),
        subtotal: cartTotal
      };

      await submitOrderRequest(orderData);
      await emptyCart();
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Navbar />
        <div style={styles.successPage}>
          <div style={styles.successCard}>
            <span style={styles.successIcon}>✅</span>
            <h2 style={styles.successTitle}>Order Request Sent!</h2>
            <p style={styles.successText}>
              Thank you! We have received your order request.
              Our team will contact you shortly on <strong>{formData.phone}</strong> to confirm your order.
            </p>
            <div style={styles.successBtns}>
              <button style={styles.primaryBtn} onClick={() => navigate('/')}>
                Back to Home
              </button>
              <button style={styles.secondaryBtn} onClick={() => navigate('/products')}>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Navbar />
        <div style={styles.successPage}>
          <div style={styles.successCard}>
            <span style={styles.successIcon}>🛒</span>
            <h2 style={styles.successTitle}>Your cart is empty</h2>
            <p style={styles.successText}>Add some products before placing an order request.</p>
            <button style={styles.primaryBtn} onClick={() => navigate('/products')}>
              Shop Now
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Place Order Request</h1>
          <p style={styles.subtitle}>Fill in your details and we'll contact you to confirm your order</p>
        </div>

        <div style={styles.grid}>
          {/* Order Summary */}
          <div style={styles.summary}>
            <h2 style={styles.sectionTitle}>Order Summary</h2>
            <div style={styles.summaryCard}>
              {cart.map((item, i) => (
                <div key={i} style={styles.cartItem}>
                  <div style={styles.itemInfo}>
                    <p style={styles.itemBrand}>{item.product.brand}</p>
                    <p style={styles.itemName}>{item.product.name}</p>
                    <p style={styles.itemSize}>{item.product.size}</p>
                  </div>
                  <div style={styles.itemRight}>
                    <p style={styles.itemQty}>x{item.quantity}</p>
                    <p style={styles.itemPrice}>
                      $ {((item.product.discountedPrice || item.product.price) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}

              <div style={styles.divider} />

              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Subtotal</span>
                <span style={styles.totalValue}>$ {cartTotal.toLocaleString()}</span>
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Shipping</span>
                <span style={styles.totalValue}>{cartTotal > 15000 ? 'Free' : '$ 500'}</span>
              </div>
              <div style={styles.divider} />
              <div style={styles.totalRow}>
                <span style={{ ...styles.totalLabel, color: '#1a1a2e', fontWeight: 'bold', fontSize: '16px' }}>Total</span>
                <span style={{ ...styles.totalValue, color: '#e63946', fontWeight: 'bold', fontSize: '18px' }}>
                  $ {(cartTotal + (cartTotal > 15000 ? 0 : 500)).toLocaleString()}
                </span>
              </div>

              <div style={styles.noteBox}>
                <p style={styles.noteText}>
                  💡 This is an order request. Our team will call you to confirm availability and finalize the order.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Your Details</h2>
            <div style={styles.formCard}>
              {error && <div style={styles.errorBox}>⚠️ {error}</div>}

              <form onSubmit={handleSubmit}>
                <div style={styles.formRow}>
                  <div style={styles.field}>
                    <label style={styles.label}>Full Name *</label>
                    <input
                      style={styles.input}
                      type="text"
                      name="customerName"
                      placeholder="Your full name"
                      value={formData.customerName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Phone Number *</label>
                    <input
                      style={styles.input}
                      type="text"
                      name="phone"
                      placeholder="+92 300 1234567"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Email Address</label>
                  <input
                    style={styles.input}
                    type="email"
                    name="email"
                    placeholder="your@email.com (optional)"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Delivery Address *</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="address"
                    placeholder="Street address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>City *</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="city"
                    placeholder="Your city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Additional Message</label>
                  <textarea
                    style={{ ...styles.input, height: '100px', resize: 'vertical' }}
                    name="message"
                    placeholder="Any special requests or notes..."
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                <button
                  style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Sending Request...' : '📩 Send Order Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container: { maxWidth: '1100px', margin: '0 auto', padding: '40px 20px' },
  header: { textAlign: 'center', marginBottom: '40px' },
  title: { color: '#1a1a2e', fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' },
  subtitle: { color: '#666', fontSize: '16px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '32px' },
  sectionTitle: { color: '#1a1a2e', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' },
  summaryCard: { background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0' },
  cartItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #f0f2f5' },
  itemInfo: {},
  itemBrand: { color: '#e63946', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', margin: 0 },
  itemName: { color: '#1a1a2e', fontSize: '14px', fontWeight: '600', margin: '2px 0' },
  itemSize: { color: '#888', fontSize: '12px', margin: 0 },
  itemRight: { textAlign: 'right' },
  itemQty: { color: '#888', fontSize: '12px', margin: 0 },
  itemPrice: { color: '#1a1a2e', fontSize: '14px', fontWeight: 'bold', margin: '2px 0' },
  divider: { height: '1px', background: '#f0f2f5', margin: '12px 0' },
  totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px' },
  totalLabel: { color: '#888', fontSize: '14px' },
  totalValue: { color: '#1a1a2e', fontSize: '14px', fontWeight: '600' },
  noteBox: { background: '#fff8e1', borderRadius: '8px', padding: '12px', marginTop: '16px', border: '1px solid #ffe082' },
  noteText: { color: '#856404', fontSize: '13px', margin: 0, lineHeight: 1.6 },
  formSection: {},
  formCard: { background: '#fff', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0' },
  errorBox: { background: '#fff5f5', color: '#e63946', border: '1px solid #ffd0d3', borderRadius: '10px', padding: '12px 16px', fontSize: '14px', marginBottom: '20px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', color: '#555', fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
  input: { width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e8eaf0', background: '#f8f9fa', color: '#333', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  submitBtn: { width: '100%', padding: '14px', background: '#e63946', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 16px rgba(230,57,70,0.3)' },
  successPage: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: '40px 20px' },
  successCard: { background: '#fff', borderRadius: '20px', padding: '48px 40px', textAlign: 'center', maxWidth: '480px', boxShadow: '0 8px 40px rgba(0,0,0,0.1)', border: '1px solid #e8eaf0' },
  successIcon: { fontSize: '64px', display: 'block', marginBottom: '20px' },
  successTitle: { color: '#1a1a2e', fontSize: '26px', fontWeight: 'bold', marginBottom: '12px' },
  successText: { color: '#666', fontSize: '15px', lineHeight: 1.7, marginBottom: '28px' },
  successBtns: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn: { padding: '12px 28px', background: '#e63946', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' },
  secondaryBtn: { padding: '12px 28px', background: '#f0f2f5', color: '#444', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px' },
  summary: {}
};

export default OrderRequestPage;